import { ResponsiveAreaBump, ResponsiveBump } from '@nivo/bump';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { fromDateKey, toDateKey } from '../../services/time';
import { MonitoredBlogWithStats } from '../../types/Ranking';
import { onlyOnClientSide } from '../ClientSidePage';
import { THEME } from './theme';

export const toChartData = <
  T extends {
    id: string;
    lastDateKey: string;
    scoreTrend: number[];
    scoresByDay: number[];
  }
>(
  data: T[],
  variant: 'absolute' | 'aggregate',
  range: number // Max is 30 - will clamp
): RankChartD[] => {
  return data.map((d) => {
    const series = variant === 'absolute' ? d.scoresByDay : d.scoreTrend;
    const slicedSeries = series.slice(-Math.min(range, 30));
    return {
      id: d.id,
      data: slicedSeries.map((v, i) => ({
        y: v,
        x: toDateKey(
          fromDateKey(d.lastDateKey).minus({
            days: slicedSeries.length - 1 - i
          })
        )
      }))
    };
  });
};

export const blogsToChartData = (
  data: MonitoredBlogWithStats[],
  variant: 'absolute' | 'aggregate',
  range: number // Max is 30 - will clamp
) => {
  return toChartData(
    data.slice(0, 15).map((d) => ({
      id: d.name,
      ...d.stats
    })),
    variant,
    range
  );
};

export type RankChartD = {
  id: string;
  data: { x: string; y: number }[];
};
const CustomPoint = ({
  data,
  x,
  y,
  isActive,
  isInactive,
  size,
  color,
  borderColor,
  borderWidth
}: {
  data: { y: number; x: string };
  x: number;
  y: number;
  isActive: boolean;
  isInactive: boolean;
  size: number;
  color: string;
  borderColor: string;
  borderWidth: number;
}) => {
  return (
    <g transform={`translate(${x}, ${y})`} style={{ pointerEvents: 'none' }}>
      <rect
        x={size * -0.5 - 4}
        y={size * -0.5 + 4}
        width={size + borderWidth}
        height={size + borderWidth}
        fill="rgba(0, 0, 0, .07)"
      />
      <rect
        x={size * -0.5}
        y={size * -0.5}
        width={size}
        height={size}
        fill={color}
        stroke={borderColor}
        strokeWidth={borderWidth}
      />
      {isActive && (
        <text textAnchor="middle" y={4} fill={borderColor}>
          {data.y}
        </text>
      )}
      {isInactive && <text textAnchor="middle" y={4} fill={borderColor}></text>}
    </g>
  );
};

export const RankChart = onlyOnClientSide(
  ({ data, height = 600 }: { data: RankChartD[]; height?: number }) => {
    // The chart wants a "perfect" rank (no missing data, no ties, everything within bounds)
    // We therefore need to recalculate all ranks within
    const rankedData = useMemo(() => {
      if (!data.length) {
        return [];
      }
      const results: { [key: string]: number[] } = {};
      const length = data[0].data.length;
      for (let idx = 0; idx < length; idx++) {
        sortBy(data, (d) => -d.data[idx].y).forEach((d, i) => {
          const res = (results[d.id] = results[d.id] || []);
          res.push(i + 1);
        });
      }
      return data.map((d) => ({
        ...d,
        data: d.data.map((t, i) => ({
          ...t,
          y: results[d.id][i]
        }))
      }));
    }, [data]);
    return (
      <div style={{ height }}>
        <ResponsiveBump
          theme={THEME}
          // @ts-ignore
          pointComponent={CustomPoint}
          data={rankedData}
          pointSize={20}
          activePointSize={24}
          inactivePointSize={16}
          pointBorderWidth={2}
          activePointBorderWidth={4}
          inactivePointBorderWidth={1}
          pointColor={'#000'}
          pointBorderColor={{ from: 'serie.color' }}
          margin={{ top: 40, right: 140, bottom: 40, left: 40 }}
          colors={{ scheme: 'nivo' }}
          axisTop={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: -36
          }}
          axisRight={null}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 32
          }}
          axisLeft={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'ranking',
            legendPosition: 'middle',
            legendOffset: -34
          }}
        />
      </div>
    );
  }
);

export const RankAreaChart = onlyOnClientSide(
  ({ data, height = 600 }: { data: RankChartD[]; height?: number }) => {
    return (
      <div style={{ height }}>
        <ResponsiveAreaBump
          theme={THEME}
          data={data}
          align="end"
          margin={{ top: 40, right: 140, bottom: 40, left: 40 }}
          spacing={10}
          colors={{ scheme: 'nivo' }}
          blendMode="normal"
          axisTop={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: -36
          }}
          axisBottom={{
            tickSize: 5,
            tickPadding: 5,
            tickRotation: 0,
            legend: '',
            legendPosition: 'middle',
            legendOffset: 32
          }}
          // defs={[
          //   {
          //     id: 'dots',
          //     type: 'patternDots',
          //     background: 'inherit',
          //     color: '#38bcb2',
          //     size: 4,
          //     padding: 1,
          //     stagger: true
          //   },
          //   {
          //     id: 'lines',
          //     type: 'patternLines',
          //     background: 'inherit',
          //     color: '#eed312',
          //     rotation: -45,
          //     lineWidth: 6,
          //     spacing: 10
          //   }
          // ]}
          /*
        fill={[
            {
                match: {
                    id: 'CoffeeScript'
                },
                id: 'dots'
            },
            {
                match: {
                    id: 'TypeScript'
                },
                id: 'lines'
            }
        ]}
        */
        />
      </div>
    );
  }
);
